import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, pluck } from 'rxjs/operators';

import { Helpers } from '@recruitee/common';

import { environment } from '../../environments/environment';
import { CompanyDataResponse, CompanySize, RecruitmentInvolvement } from '../company/company.types';
import { TimeFormat } from '../onboarding.types';
import { TimeFormatResponse, UserDataResponse } from '../user/user.types';

type CompanyRequest = {
  emailConfirmation: {
    data: {
      companyLanguage: string;
      companySize: CompanySize;
      adminRole: string;
      familiarWithAts: boolean;
      recruitmentInvolvement: RecruitmentInvolvement;
      invitedEmails?: string[];
      timeFormat24: boolean;
      invitedEmailBodyHtml?: string;
      fillWithSampleData: boolean;
    };
    token: string;
  };
};

type UserRequest = {
  admin: {
    firstName: string;
    lastName: string;
    password: string;
    email: string;
    phone: string;
    avatar?: File;
    timeFormat24: boolean;
    preferredLanguage: string;
    invitationToken: string;
  };
};

interface TrackingData extends Record<any, any> {
  company: { createdAt: number; id: number; name: string };
  companyId: number;
  companyName: string;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  name: string;
  phone: string;
}

export type PatchCompanyResponse = {
  code: string;
  companyId: number;
  email: string;
  trackingData: TrackingData;
};

@Injectable()
export class OnboardingApiService {
  constructor(private http: HttpClient) {}

  public patchCompany(data: CompanyRequest): Observable<PatchCompanyResponse> {
    return <Observable<PatchCompanyResponse>>(
      this.http
        .patch(environment.apiCompanyUrl, Helpers.underscoreKeys(data))
        .pipe(map(res => Helpers.camelizeKeys(res)))
    );
  }

  public createUser(data: UserRequest): Observable<unknown> {
    return <Observable<unknown>>(
      this.http.post(environment.apiUserUrl, Helpers.underscoreKeys(data))
    );
  }

  public getUserData(token: string): Observable<UserDataResponse> {
    return <Observable<UserDataResponse>>(
      this.http
        .get(`${environment.apiUserDataUrl}${token}`)
        .pipe(map(data => Helpers.camelizeKeys(data)))
    );
  }

  public getCompanyData(token: string): Observable<CompanyDataResponse> {
    return <Observable<CompanyDataResponse>>(
      this.http
        .get(`${environment.apiCompanyDataUrl}${token}`)
        .pipe(map(data => Helpers.camelizeKeys(data)))
    );
  }

  public getCountryCode(): Observable<string> {
    return <Observable<string>>this.http.get(environment.apiCountryCodeUrl, {
      responseType: 'text',
    });
  }

  public getDefaultTimeFormat(): Observable<TimeFormatResponse> {
    return <Observable<TimeFormatResponse>>this.http.get(`${environment.apiTimeFormat24Url}`).pipe(
      map(data => Helpers.camelizeKeys(data)),
      pluck('timeFormat24'),
      map((timeFormat24: boolean) => (timeFormat24 ? TimeFormat.Hour24 : TimeFormat.Hour12)),
      map((timeFormat: TimeFormat) => ({ timeFormat })),
    );
  }
}
